import React, { useReducer, useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FakeText, Card } from '@totalsoft/rocket-ui';
import ApprovalTypeGeneralDataComponent from './ApprovalTypeGeneralDataComponent';
import ApprovalTypeLevelListComponent from './ApprovalTypeLevelListComponent';
import ApprovalTypeDecisionReasonsListComponent from './reasons/ApprovalTypeDecisionReasonsListComponent';
import { reducer, initialState } from '../../reducers/approvalType/containerReducer';
import { useValidation } from '@totalsoft/pure-validations-react';
import { approvalTypeValidator } from 'features/approvalTypes/validators';
import { isValid } from '@totalsoft/pure-validations-react';
import { CompetenceOrderDecisionBehaviorCodes } from '../../constants';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { APPROVAL_TYPE_QUERY, DECISION_BEHAVIORS_QUERY } from './queries';
import { ADD_UPDATE_APPROVAL_TYPE, DELETE_APPROVAL_TYPE_LEVEL } from './mutations';
import { APPROVAL_TYPE_UPDATED, APPROVAL_TYPE_LEVEL_DELETED } from './subscriptions';
import { useToast } from 'hooks/toasts/useToast';

const ApprovalTypeContainer = ({ approvalTypeId, siteId, onEdit, onAdd, onSaveFnChanged, onRedirect }) => {
  const { t } = useTranslation();
  const addToast = useToast();

  const [state, dispatch] = useReducer(reducer, initialState);
  const [rendered, setRendered] = useState(false);
  const [approvalTypeValidation, validateApprovalType] = useValidation(approvalTypeValidator);

  const { data, loading } = useQuery(APPROVAL_TYPE_QUERY, {
    variables: { approvalTypeId, siteId },
    skip: approvalTypeId === undefined || siteId === undefined
  });

  const { data: decisionBehaviorsData, loading: decisionBehaviorsLoading } = useQuery(DECISION_BEHAVIORS_QUERY, {
    variables: { siteId: siteId || 0 }
  });

  const [addOrUpdateApprovalType] = useMutation(ADD_UPDATE_APPROVAL_TYPE);

  const [deleteApprovalTypeLevel] = useMutation(DELETE_APPROVAL_TYPE_LEVEL);

  useSubscription(APPROVAL_TYPE_UPDATED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('ApprovalTypes.ApprovalTypeUpdated'), 'success');
      dispatch({ type: 'OnHeaderSaved', reasons: data.approvalTypeUpdated.approvalType.approvalTypeDecisionReasons });
      if (approvalTypeId === undefined)
        onRedirect(data.approvalTypeUpdated.approvalType.approvalTypeId, data.approvalTypeUpdated.approvalType.siteId);
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedApprovalTypes' });
      client.cache.gc();
    }
  });

  useSubscription(APPROVAL_TYPE_LEVEL_DELETED, {
    onData: ({ data: { data }, client }) => {
      client.cache.evict({
        id: `ApprovalTypeLevelItem:{"approvalTypeLevelId":${data.approvalTypeLevelDeleted.approvalTypeLevelId}}`
      });
      client.cache.gc();
      addToast(t('ApprovalTypes.ApprovalTypeLevelDeleted'), 'success');
    }
  });

  const onSave = useCallback(() => {
    // eslint-disable-next-line no-unused-vars
    const { changed, saving, ...approvalType } = state.header;
    if (!validateApprovalType(state.header)) return;
    dispatch({ type: 'OnBeforeSaved' });
    return addOrUpdateApprovalType({
      variables: { input: { ...approvalType, approvalTypeDecisionReasons: state.approvalTypeDecisionReasons } }
    });
  }, [addOrUpdateApprovalType, state.approvalTypeDecisionReasons, state.header, validateApprovalType]);

  const onDelete = useCallback(
    (approvalTypeId, approvalTypeLevelId, index) => {
      dispatch({ type: 'OnDelete', index });
      deleteApprovalTypeLevel({
        variables: { input: { approvalTypeId, approvalTypeLevelId } }
      });
    },
    [deleteApprovalTypeLevel]
  );

  useEffect(() => {
    setRendered(true);
  }, [rendered]);

  useEffect(() => {
    if (data && data.approvalTypeItem) dispatch({ type: 'OnInitState', approvalType: data.approvalTypeItem });
    else if (decisionBehaviorsData && decisionBehaviorsData.decisionBehaviors && approvalTypeId === undefined) {
      const behaviorWithoutReasonId = decisionBehaviorsData.decisionBehaviors.find(
        x => x.decisionBehaviorCode.localeCompare(CompetenceOrderDecisionBehaviorCodes.WithoutReason) === 0
      )?.decisionBehaviorId;
      dispatch({ type: 'OnApprovalTypeAdded', behaviorWithoutReasonId: behaviorWithoutReasonId });
    }
  }, [approvalTypeId, data, decisionBehaviorsData]);

  useEffect(() => {
    if (!rendered) return;
    onSaveFnChanged(onSave, !state.header.changed || !(approvalTypeValidation |> isValid));
  }, [onSave, state.header.changed, rendered, onSaveFnChanged, approvalTypeValidation]);

  useEffect(() => {
    validateApprovalType(state.header);
  }, [state.header, validateApprovalType]);

  const decisionBehaviorCode = decisionBehaviorsData?.decisionBehaviors.find(
    x => x.decisionBehaviorId === state.header.decisionBehaviorId
  )?.decisionBehaviorCode;

  return !loading ? (
    <>
      <ApprovalTypeGeneralDataComponent
        approvalTypeItem={state.header}
        dispatch={dispatch}
        changed={state.header.changed}
        validation={approvalTypeValidation}
        competenceOrderDecisionBehaviorOptions={decisionBehaviorsData ? decisionBehaviorsData.decisionBehaviors : []}
      />
      {approvalTypeId !== null && approvalTypeId !== undefined && (
        <ApprovalTypeLevelListComponent approvalTypeLevels={state.approvalTypeLevels} onEdit={onEdit} onAdd={onAdd} onDelete={onDelete} />
      )}
      {!loading &&
        !decisionBehaviorsLoading &&
        (decisionBehaviorCode?.localeCompare(CompetenceOrderDecisionBehaviorCodes.WithReason) === 0 ||
          decisionBehaviorCode?.localeCompare(CompetenceOrderDecisionBehaviorCodes.OnlyOneReason) === 0) && (
          <ApprovalTypeDecisionReasonsListComponent approvalTypeDecisionReasons={state.approvalTypeDecisionReasons} dispatch={dispatch} />
        )}
    </>
  ) : (
    <Card>
      <FakeText lines={10} />
    </Card>
  );
};

ApprovalTypeContainer.propTypes = {
  approvalTypeId: PropTypes.number,
  siteId: PropTypes.number,
  onEdit: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  onSaveFnChanged: PropTypes.func.isRequired,
  onRedirect: PropTypes.func.isRequired
};

export default ApprovalTypeContainer;
